import { LaunchStatusBadge, Skeleton } from "@/shared/components/ds";
import type { EngagementTeamMember, LaunchMain } from "../models/launchDetail";
import { daysBetween, STOP_DAY } from "./dashboardFilters";
import styles from "./dashboard.module.css";

const ROLE = { om: /^OM\b|ops/i, em: /^EM\b|engagement/i };

function member(team: EngagementTeamMember[] | null | undefined, role: RegExp): string | null {
    return team?.find((m) => role.test(m.role ?? ""))?.name || null;
}

/** The last day the launch served: its end, or the day the old platform stopped if it never ended. */
export function lastDay(main: LaunchMain): string {
    return main.end_date && main.end_date < STOP_DAY ? main.end_date : STOP_DAY;
}

/** How many days the launch was live by shutdown; the warehouse's duration when the dates are missing. */
export function finalDaysLive(main: LaunchMain): number | null {
    if (main.start_date) return Math.max(0, daysBetween(main.start_date, lastDay(main)));
    return main.duration_in_days ?? null;
}

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className={styles.summaryCell}>
            <div className={styles.micro}>{label}</div>
            <div className={styles.summaryValue}>{children}</div>
        </div>
    );
}

function Person({ name }: { name: string | null }) {
    return name ? <>{name}</> : <span className={styles.unassigned}>Unassigned</span>;
}

/** The launch at a glance, above the filters (MPN-2230): who, when, how long, and its status at shutdown. */
export function SummaryStrip({ main }: { main: LaunchMain | undefined }) {
    if (!main) {
        return (
            <div className={styles.summary} aria-busy="true">
                {["Affiliate", "Status at Shutdown", "Start Date", "End Date", "Final Days Live", "Ops Manager", "Eng. Manager", "Type"].map((label) => (
                    <div key={label} className={styles.summaryCell}>
                        <div className={styles.micro}>{label}</div>
                        <Skeleton width={70} style={{ marginTop: 8 }} />
                    </div>
                ))}
            </div>
        );
    }
    const days = finalDaysLive(main);
    return (
        <div className={styles.summary}>
            <Cell label="Affiliate">{main.affiliate_name || "—"}</Cell>
            <Cell label="Status at Shutdown"><LaunchStatusBadge active={main.is_active} /></Cell>
            <Cell label="Start Date">{main.start_date || "—"}</Cell>
            <Cell label="End Date">{main.end_date || "—"}</Cell>
            <Cell label="Final Days Live">{days === null ? "—" : days.toLocaleString("en-US")}</Cell>
            <Cell label="Ops Manager"><Person name={member(main.engagement_team, ROLE.om)} /></Cell>
            <Cell label="Eng. Manager"><Person name={member(main.engagement_team, ROLE.em)} /></Cell>
            <Cell label="Type">{main.revenue_type || "—"}</Cell>
        </div>
    );
}
